import { useContext, useEffect, useRef, useState } from "react";
import io from "socket.io-client";
import { UserContext } from "../context/UserContext";
import { RecipeContext } from "../context/RecipeContext";
import API from "../api";
import { BASEURL, protocol } from "../api";
import useLocalStorage from "./useLocalStorage";
import { Book } from "../utils/types";

/** Custom Hook to create open connection between client and server
 *
 * Notes: Should auto connect be turned off?
 *
 * [MainContainer, RecipesList] -> useWebSocket
 */

function useWebSocket() {
  const [socket, setSocket] = useState(null);
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState(null);
  const [bookId, setBookId] = useLocalStorage("current-book-id");

  const { userId, user, currentBook, setUserData, defaultBookId, books } =
    useContext(UserContext);
  const { selectedRecipe, updateRecipes } = useContext(RecipeContext);

  const syncedValues = useRef({
    userId,
    user,
    currentBook,
    defaultBookId,
    selectedRecipe,
  });

  useEffect(() => {
    syncedValues.current = {
      userId,
      user,
      currentBook,
      defaultBookId,
      selectedRecipe,
    };
  }, [userId, user, currentBook, defaultBookId, selectedRecipe]);

  /** Initiates handshake, maintains connection, & disconnects on unmount */
  useEffect(() => {
    const newSocket = io(`${protocol}://${BASEURL}`, {
      auth: { userId: userId, token: API.token },
    });

    setSocket(newSocket);
    newSocket.on("connect", () => {});

    newSocket.on("book_shared", (data) => {
      setMessage(data.message);
    });

    newSocket.on("recipe_shared", (data) => {
      setMessage(data.message);
    });

    newSocket.on("user_shared_book", (data) => {
      setMessage(data.message);
      if (!defaultBookId) {
        setUserData((prevState) => {
          const newState = {
            ...prevState,
            books: [data.payload],
            defaultBook: data.payload,
            defaultBookId: data.payload.id,
            currentBook: data.payload,
            currentBookId: data.payload.id,
          };
          setBookId(data.payload.id);
          return newState;
        });
      }
      setUserData((prevState) => ({ ...prevState, books: data.books }));
      setStatus(200);
    });

    newSocket.on("user_shared_recipe", (data) => {
      const { currentBook } = syncedValues.current;
      if (data.payload) {
        setUserData((prevState) => {
          const newState = {
            ...prevState,
            books: [...prevState.books, data.payload],
            defaultBook: data.payload,
            defaultBookId: data.payload.id,
            currentBook: data.payload,
            currentBookId: data.payload.id,
          };
          setBookId(data.payload.id);
          return newState;
        });
      }
      if (currentBook?.book_type === "shared_inbox") updateRecipes(data.recipe);
      setMessage(data.message);
      setStatus(200);
    });

    newSocket.on("error_sharing_book", (data) => {
      setMessage(data.data);
    });

    newSocket.on("error_sharing_recipe", (data) => {
      setMessage(data.data);
    });

    return () => {
      newSocket.disconnect();
      // console.log("Disconnected from server");
    };
  }, []);

  /** Sends message to share book with recipient */
  function sendBook(recipient: string, privileges: string) {
    if (socket && recipient) {
      socket.emit("share_book", {
        userId,
        recipient,
        user,
        currentBook,
        privileges,
      });
    }
  }

  function sendRecipe(recipient: string) {
    if (socket && recipient) {
      socket.emit("share_recipe", {
        recipient,
        recipeId: selectedRecipe.id,
        user,
        recipeName: selectedRecipe.name,
      });
    }
  }

  /** Resets message */
  function resetMessage() {
    setMessage("");
    setStatus(null);
  }
  return { sendRecipe, sendBook, message, resetMessage, status };
}

export default useWebSocket;
